"use client";

import { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import type { JudgeResponse } from "@/app/lib/types";
import MarkdownRenderer from "./MarkdownRenderer";
import { formatDuration, humanizeModel } from "@/app/lib/format";
import {
  judgeElapsedSeconds,
  selectJudgeDisplayStart,
  type JudgeDisplayStart,
} from "@/app/lib/judgeClock";
import { buildSpokenJudgeText, judgeSpeechCardId } from "@/app/lib/judgeSpeech";

// Mirrors iOS JudgeCardView (4-state model). States:
//   waiting  — auto-judge tap landed; providers still streaming.
//   scoring  — judge call in flight, no scores yet.
//   synth    — scoring complete, synthesis streaming.
//   complete — verdict landed.

interface JudgeCardProps {
  verdict: JudgeResponse | null;
  providers: Array<{ id: string; display_name: string; color: string }>;
  isLoading: boolean;
  /** Server-authoritative start stamp, used when this card mounts mid-judge. */
  judgeStartedAt?: string;
  /** Provider ID picked to score this judge run (live in-flight scorer). When
   *  cleared post-verdict, `verdict.scoring_provider` is the persisted source. */
  scoringProvider?: string | null;
  /** True only for auto-judge runs in the gap between composer-Judge tap and
   *  the /judge SSE call kicking off (providers still streaming). Drives the
   *  "Waiting for output…" copy. */
  isWaitingForOutputs?: boolean;
  /** When true, the body uses ResponseCard's streaming-context height calc
   *  (subtracts `--query-area-h`) so heights match exactly when rendered
   *  side-by-side with streaming provider cards. Default false (matches
   *  ResponseCard's complete-context calc, used in the chatHistory render
   *  branch where the timestamp+prompt is per-record above the cards). */
  isStreamingContext?: boolean;
  onDelete?: () => void;
  onRegenerate?: () => void;
  /** Desktop-web tap-to-expand: when provided, the card header becomes
   *  clickable and fires this. Parent renders the CardExpandPopup with the
   *  same JudgeCard inside (sized via `inPopup`). */
  onExpand?: () => void;
  /** When true, body uses generous popup-height (no viewport calc) so the
   *  user sees the full verdict/synthesis. Used by the expand-popup wrapper. */
  inPopup?: boolean;
  /** The parent derives this asynchronously with the server-compatible hash. */
  promptHash?: string;
  isStatsExcluded?: boolean;
  /** Optimistically updates the page-owned judge-stats exclusion state. */
  onSetStatsExcluded?: (promptHash: string, excluded: boolean) => void;
  // TTS read-aloud props (optional — hidden when omitted). iOS speaks the
  // full verdict and keys its cache target by the winner-specific card id.
  recordId?: string;
  onReadAloud?: (recordId: string, providerId: string, text: string) => void;
  isTTSActive?: boolean;
  ttsProgress?: number;
  // True when the cache already holds the synthesis audio — drives the
  // solid-vs-hollow PLAY-triangle icon (matches iOS; see ResponseCard's
  // isTTSCached for the equivalent semantics).
  isTTSCached?: boolean;
}

const HEADER_HEIGHT = 64;
// Meta line typography matches ResponseCard Row 2 (10px monospaced) so the
// two card kinds align visually side-by-side. No shrink-to-fit — instead,
// we render full labels by default and switch to abbreviated section
// headers (J:, S:, T:) only when content overflows the 2-line clamp.
const META_FONT_SIZE = 10;
// Single space between value and duration within a section (e.g. "Judge: X 22s").
// Was previously a hair-space-dot-hair-space separator, but the dots took
// ~3 chars each without adding much info — the pipe BAR between sections
// is enough visual separation, and the duration unit (s, m) makes intent clear.
const DOT = " ";
const BAR = " | ";

export const JUDGE_HEADER_HEIGHT = HEADER_HEIGHT;

// Trophy SVG sized to match provider icons (~14px). Outline + fill style.
function TrophyIcon({ color, size = 14 }: { color: string; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill={color}
      style={{ flexShrink: 0 }}
      aria-hidden
    >
      <path d="M4 2h8v3a4 4 0 0 1-3 3.87V10h2v1H5v-1h2V8.87A4 4 0 0 1 4 5V2zm-1 1H1.5A.5.5 0 0 0 1 3.5v1A2.5 2.5 0 0 0 3.5 7h.05A4 4 0 0 1 3 5V3zm10 0v2a4 4 0 0 1-.55 2h.05A2.5 2.5 0 0 0 15 4.5v-1a.5.5 0 0 0-.5-.5H13zM4 12h8a1 1 0 0 1 1 1v1H3v-1a1 1 0 0 1 1-1z" />
    </svg>
  );
}

export default function JudgeCard({
  verdict,
  providers,
  isLoading,
  judgeStartedAt,
  scoringProvider,
  isWaitingForOutputs = false,
  isStreamingContext = false,
  onDelete,
  onRegenerate,
  onExpand,
  inPopup,
  promptHash,
  isStatsExcluded = false,
  onSetStatsExcluded,
  recordId,
  onReadAloud,
  isTTSActive,
  ttsProgress,
  isTTSCached,
}: JudgeCardProps) {
  // All hooks declared up-front so React sees the same hook count regardless
  // of whether we early-return below. Switching verdict from null → present
  // (during a live judge run) is the exact case that breaks if hooks live
  // after a conditional return.
  const [didCopy, setDidCopy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const metaRef = useRef<HTMLSpanElement>(null);
  const clientJudgeStartedAtRef = useRef(Date.now());
  const displayJudgeStartedAtRef = useRef<JudgeDisplayStart | null>(null);
  const [elapsedNow, setElapsedNow] = useState(() => Date.now());
  const isJudgeInFlight = isLoading && !verdict;

  if (isJudgeInFlight) {
    displayJudgeStartedAtRef.current = selectJudgeDisplayStart(
      judgeStartedAt,
      clientJudgeStartedAtRef.current,
      elapsedNow,
      displayJudgeStartedAtRef.current
    );
  }

  const elapsedSeconds = isJudgeInFlight
    ? judgeElapsedSeconds(
        displayJudgeStartedAtRef.current?.start ?? clientJudgeStartedAtRef.current,
        elapsedNow
      )
    : 0;

  // The clock starts immediately and is torn down both when a verdict lands
  // and when the card unmounts.
  useEffect(() => {
    if (!isJudgeInFlight) return;
    setElapsedNow(Date.now());
    const interval = setInterval(() => setElapsedNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [isJudgeInFlight]);

  // Pre-scoring rotation tick — increments every 3s while the panel is
  // still scoring. Used to cycle the "Judging…" text + trophy icon
  // through the 3 scorer colors (anthropic/gemini/openai), mirroring
  // iOS JudgeCardView's TimelineView pattern. Reflects that all three
  // panel scorers are running concurrently. Stops naturally when the
  // score line replaces the spinner (`hasScores` flips true).
  const [rotationTick, setRotationTick] = useState(0);

  const handleReadAloudJudge = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!onReadAloud || !recordId || !verdict?.synthesis) return;
    onReadAloud(
      recordId,
      judgeSpeechCardId(verdict),
      buildSpokenJudgeText(verdict, (id) => providers.find((provider) => provider.id === id)?.display_name),
    );
  }, [onReadAloud, providers, recordId, verdict]);

  const handleCopyText = useCallback((text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setDidCopy(true);
      setTimeout(() => setDidCopy(false), 1200);
    });
  }, []);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent, hasVerdict: boolean) => {
      if (!hasVerdict) return;
      e.preventDefault();
      setMenuPos({ x: e.clientX, y: e.clientY });
      setMenuOpen(true);
    },
    []
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent, hasVerdict: boolean) => {
      if (!hasVerdict) return;
      const touch = e.touches[0];
      longPressTimer.current = setTimeout(() => {
        setMenuPos({ x: touch.clientX, y: touch.clientY });
        setMenuOpen(true);
      }, 500);
    },
    []
  );

  const handleTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    document.addEventListener("click", close);
    document.addEventListener("contextmenu", close);
    return () => {
      document.removeEventListener("click", close);
      document.removeEventListener("contextmenu", close);
    };
  }, [menuOpen]);

  // Adaptive meta line — four progressive shorten levels:
  //   0 = full-with-model      (Synthesis: Sonnet 4.6 1m 0s)
  //   1 = full-no-model        (Synthesis: 1m 0s)
  //   2 = full labels
  //   3 = abbreviated Judging/Synthesis labels; total remains spelled out
  // Render starts at level 0; the layout effect measures overflow and
  // increments level until content fits or hits 3 (then ellipsis takes over).
  const [metaLevel, setMetaLevel] = useState<0 | 1 | 2 | 3>(0);
  // Reset to level 0 when content changes so we re-test from the most
  // generous form. Without this, a once-overflowed render stays shrunken
  // forever even after the content gets shorter.
  const lastContentKeyRef = useRef("");
  const contentKey = `${verdict?.winner ?? ""}|${(verdict?.winners ?? []).join(",")}|${verdict?.scoring_provider ?? ""}|${verdict?.synthesis_duration_ms ?? ""}|${verdict?.scoring_duration_ms ?? ""}|${verdict?.total_duration_ms ?? ""}|${verdict?.synthesis_model ?? ""}|${isLoading}|${isWaitingForOutputs}|${scoringProvider ?? ""}`;
  useLayoutEffect(() => {
    const el = metaRef.current;
    if (!el) return;
    if (lastContentKeyRef.current !== contentKey) {
      lastContentKeyRef.current = contentKey;
      if (metaLevel !== 0) {
        setMetaLevel(0);
        return;
      }
    }
    if (metaLevel < 3 && el.scrollHeight > el.clientHeight + 1) {
      setMetaLevel((metaLevel + 1) as 0 | 1 | 2 | 3);
    }
  });

  // Width-change re-evaluation. When the card grows (sidebar collapsed,
  // window resized wider, viewport rotated to landscape), reset metaLevel
  // to 0 so the layout effect above re-tests from the most generous form.
  // It'll only shrink again if the new width still doesn't fit. Without
  // this, once a card abbreviates it stays abbreviated even after space
  // becomes available.
  useEffect(() => {
    const el = metaRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      // Reset is a no-op if metaLevel is already 0, so this is cheap.
      setMetaLevel(0);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Pre-scoring color rotation interval. Ticks every 3s while the
  // panel is scoring. Cleaned up when the phase ends OR the card
  // unmounts. Effect deps include `isPreScoringPhaseForEffect` so
  // we don't run an interval after scores land or the card
  // transitions to verdict-rendered state.
  const isPreScoringPhaseForEffect =
    isWaitingForOutputs ||
    (isLoading && !(verdict?.scores && Object.keys(verdict.scores).length > 0));
  useEffect(() => {
    if (!isPreScoringPhaseForEffect) return;
    const interval = setInterval(() => {
      setRotationTick((t) => t + 1);
    }, 3000);
    return () => clearInterval(interval);
  }, [isPreScoringPhaseForEffect]);

  // Don't render anything if there's nothing to show. Done AFTER all hooks
  // are declared so the hook order is identical across renders. Note:
  // `isWaitingForOutputs` also counts as a render trigger — the synthesis
  // card is visible from the moment the user taps composer Judge, before
  // /judge actually fires.
  if (!verdict && !isLoading && !isWaitingForOutputs) {
    return null;
  }

  // ---------- color derivation ----------
  const winnerProvider = verdict
    ? providers.find((p) => p.id === verdict.winner || p.display_name === verdict.winner)
    : null;
  const winnerColor = winnerProvider?.color || "#3b82f6";

  // Trophy color: NEUTRAL GRAY during waiting/scoring/synthesis, WINNER
  // brand color once verdict lands. Pre-panel rollout this used to switch
  // through the credited scorer's color mid-flight to "show" who was
  // judging — but with panel mode all three score in parallel, so a single
  // scorer color is misleading. Gray during the wait, satisfying winner
  // reveal at verdict. `judgeColor` retained as a name so the loading
  // spinner SVGs (rendered with that color in pre-verdict states) keep
  // their stable identifier — just points at gray now.
  const NEUTRAL_GRAY = "var(--text-tertiary, #888)";
  const judgeColor = NEUTRAL_GRAY;
  // Multi-winner verdicts (post-2026-05-09 tie rule): trophy goes
  // neutral since coloring it as one of N tied providers would be
  // misleading.
  const isTiedVerdict = !!(verdict?.winners && verdict.winners.length > 1);

  // Pre-scoring color rotation. Active iff the spinner is showing
  // (waiting for outputs OR scoring without scores yet). Derived
  // BEFORE trophyColor so the trophy can prefer the rotating color
  // when in this phase; reverts to winner / neutral once landed.
  // Order matches iOS JudgeCardView.judgingRotationOrder.
  const ROTATION_ORDER: readonly string[] = ["anthropic", "gemini", "openai"];
  const scoresHaveLanded = !!(
    verdict?.scores && Object.keys(verdict.scores).length > 0
  );
  const inPreScoringPhase = isWaitingForOutputs || (isLoading && !scoresHaveLanded);
  const rotatingColor: string | null = (() => {
    if (!inPreScoringPhase) return null;
    const pid = ROTATION_ORDER[rotationTick % ROTATION_ORDER.length];
    const p = providers.find((pr) => pr.id === pid);
    return p?.color || null;
  })();

  const trophyColor: string =
    rotatingColor
      ? rotatingColor
      : !isTiedVerdict && verdict && verdict.winner && winnerProvider
        ? winnerColor
        : NEUTRAL_GRAY;

  // ---------- score derivation ----------
  // Treat any pid in `verdict.winners` as a winner so a tied pair
  // both render with full names + winner styling. Falls back to the
  // legacy single-winner check when `winners` is absent (older
  // persisted verdicts predating the tie field).
  const winnerSet = new Set<string>(
    verdict?.winners && verdict.winners.length > 0
      ? verdict.winners
      : verdict?.winner
        ? [verdict.winner]
        : []
  );
  const scoreEntries: Array<{ name: string; score: number; isWinner: boolean }> = [];
  if (verdict?.scores) {
    for (const [pid, score] of Object.entries(verdict.scores)) {
      const p = providers.find((pr) => pr.id === pid || pr.display_name === pid);
      scoreEntries.push({
        name: p?.display_name || pid,
        score: Math.max(1, Math.min(10, score)),
        isWinner: winnerSet.has(pid),
      });
    }
    scoreEntries.sort((a, b) => {
      if (a.isWinner !== b.isWinner) return a.isWinner ? -1 : 1;
      return b.score - a.score;
    });
  }
  const hasScores = scoreEntries.length > 0;
  const synthesisInFlight = isLoading && hasScores;
  const isComplete = !isLoading && !isWaitingForOutputs && verdict;
  // States 1+2 share a render branch — pre-scoring spinner with status text.
  // `isWaitingForOutputs` (composer Judge tapped, providers still streaming,
  // /judge hasn't fired) and `isLoading && !hasScores` (judge fired, scoring
  // event hasn't arrived) both render the same shape.
  const isPreScoringSpinner = isWaitingForOutputs || (isLoading && !hasScores);
  // Match ResponseCard's body-height calc exactly so the synthesis card
  // sits at the same vertical extent as the streaming provider cards
  // beside it. Cards are viewport-anchored (static height, fill the
  // visible vertical space) — every card in the row is the same height
  // regardless of content state. ResponseCard's streaming branch
  // subtracts `--query-area-h` (the dynamic pending-prompt area height)
  // instead of a fixed offset, so when the prompt area is shorter than
  // the 70px default, the cards grow. The complete-context calc has no
  // `--query-area-h` term because the chatHistory render branch puts a
  // per-record timestamp+prompt above the cards instead of a single
  // shared pending-prompt area.
  const bodyHeight = inPopup
    // In the expand popup, let the body flow to natural height so the popup
    // is the single scroll surface — prompt rolls up first, then card body
    // continues in the same scroll motion.
    ? undefined
    // SINGLE viewport-anchored height regardless of streaming state —
    // matches ResponseCard. Subtracting `--query-area-h` made cards
    // shrink when the user's prompt was long, and the per-card flip on
    // isComplete created unequal heights across the row.
    //
    // INVARIANT: this formula MUST match ResponseCard.tsx exactly. Edit
    // both files together. See design/card_heights.md before editing.
    : "calc(100dvh - var(--bottom-bar-h, 120px) - var(--attachment-bar-h, 0px) - 160px)";

  // (Scorer name was previously surfaced in the meta line — removed
  // 2026-05-05 once panel mode shipped. The trophy color still uses the
  // resolvedScorerProvider for in-flight state.)

  // ---------- combined meta line ----------
  // Four-level adaptive output. The layout effect picks the lowest level
  // (= most info shown) that fits the 2-line clamp.
  //   level 0: full + Sonnet model name
  //     "Judge: Anthropic · 22s | Synthesis: Sonnet 4.6 · 2m 4s | total 5m 30s"
  //   level 1: full labels, model name dropped
  //     "Judge: Anthropic · 22s | Synthesis: 2m 4s | total 5m 30s"
  //   level 2: full labels
  //     "Judging 22s | Synthesis: 2m 4s | total: 5m 30s"
  //   level 3: abbreviated Judging/Synthesis labels; total stays explicit
  //     "J 22s | S: 2m 4s | total: 5m 30s"
  const buildMeta = (level: 0 | 1 | 2 | 3): string => {
    // Pre-scoring states. Meta line is intentionally blank — the header
    // already shows "Judging…" once; duplicating it in the meta row was
    // visual noise. Once the verdict lands, the meta line populates with
    // timing/synthesis info.
    if (isWaitingForOutputs) return " ";
    if (isLoading && !hasScores) return " ";
    if (!verdict) return " ";
    const abbr = level === 3;
    const showSynthModel = level === 0;

    const sections: string[] = [];
    // Section 1: Judging · <duration>. Provider names omitted; the duration
    // alone communicates the scoring step finished.
    let judgeSection = abbr ? "J" : "Judging";
    const scoringDur = formatDuration(verdict.scoring_duration_ms);
    if (scoringDur) judgeSection += `${DOT}${scoringDur}`;
    sections.push(judgeSection);

    // Section 2: Synthesis: [<model> · ]<duration>
    const synthDur = formatDuration(verdict.synthesis_duration_ms);
    if (synthDur || showSynthModel) {
      let synthSection = `${abbr ? "S" : "Synthesis"}: `;
      if (showSynthModel) {
        const synthName = humanizeModel(verdict.synthesis_model);
        // Fallback only when the server omits synthesis_model; the live path
        // always sends it (humanizeModel renders "claude-sonnet-5" → "Sonnet 5").
        // Synthesizer swapped 4.6 → 5 on 2026-07-03.
        synthSection += synthName || "Sonnet 5";
        if (synthDur) synthSection += `${DOT}${synthDur}`;
      } else if (synthDur) {
        synthSection += synthDur;
      }
      sections.push(synthSection);
    }

    // Section 3: total <duration>. Shown whenever total_duration_ms is
    // captured (auto-judge: composer-tap → verdict; post-hoc: toolbar-tap
    // → verdict). Both flows now record the press timestamp in
    // autoJudgePressedAtRef and the verdict event handler computes total.
    const totalDur = formatDuration(verdict.total_duration_ms);
    if (totalDur) sections.push(`total: ${totalDur}`);

    return sections.join(BAR);
  };
  const metaLine = buildMeta(metaLevel);
  const metaLine0 = buildMeta(0);

  // ---------- body status text ----------
  // Mirrors iOS JudgeCardView.swift:353-359 — four pre-complete states the
  // user sees beneath the spinner. Web doesn't surface isWaitingForRetry,
  // so that branch is omitted; the other three match verbatim.
  const bodyStatusText: string = (() => {
    if (isWaitingForOutputs) return "Waiting for output...";
    if (isLoading && !hasScores) return "Awaiting judging results...";
    if (synthesisInFlight) return "Synthesizing...";
    return "";
  })();

  // ---------- full-card copy text ----------
  const fullCopyText: string = (() => {
    if (!verdict) return "";
    const lines: string[] = [];
    if (scoreEntries.length > 0) {
      lines.push(scoreEntries.map((e) => `${e.name}: ${e.score}/10`).join(" | "));
    } else if (verdict.winners && verdict.winners.length > 1) {
      const names = verdict.winners.map(
        (pid) => providers.find((p) => p.id === pid)?.display_name || pid
      );
      lines.push(`Tied: ${names.join(" & ")}`);
    } else if (verdict.winner) {
      const win = providers.find((p) => p.id === verdict.winner);
      lines.push(`${win?.display_name || verdict.winner} wins`);
    }
    if (metaLine0.trim()) lines.push(metaLine0);
    if (verdict.rationale) {
      lines.push("");
      lines.push(verdict.rationale);
    }
    if (verdict.synthesis) {
      lines.push("");
      lines.push("BEST-OF SYNTHESIS");
      lines.push("");
      lines.push(verdict.synthesis);
    }
    return lines.join("\n");
  })();

  // ---------- render ----------
  return (
    <div
      ref={cardRef}
      data-judge-card
      onContextMenu={(e) => handleContextMenu(e, !!verdict)}
      onTouchStart={(e) => handleTouchStart(e, !!verdict)}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchEnd}
      style={{
        borderRadius: "var(--radius-lg)",
        border: isTTSActive
          ? `1px solid ${isComplete ? winnerColor : judgeColor}`
          : `1px solid color-mix(in srgb, ${
              isComplete ? winnerColor : judgeColor
            } 30%, transparent)`,
        boxShadow: isTTSActive
          ? `0 0 0 1px color-mix(in srgb, ${
              isComplete ? winnerColor : judgeColor
            } 50%, transparent)`
          : undefined,
        background: "#161b22",
        position: "relative",
        // In the popup: fill the card-expand-popup-body container and
        // become a flex column so the body region (one of the three
        // regimes below) flex:1 + overflow:auto fills remaining height.
        ...(inPopup
          ? {
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column" as const,
            }
          : {}),
      }}
    >
      {/* Header — locked 64px height; content top-aligned */}
      <div
        onClick={onExpand && !inPopup ? onExpand : undefined}
        data-card-header={inPopup ? "true" : undefined}
        style={{
          height: HEADER_HEIGHT,
          padding: "4px 7px",
          background: `color-mix(in srgb, ${judgeColor} 6%, #1c2129)`,
          borderRadius: "var(--radius-lg) var(--radius-lg) 0 0",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          justifyContent: "flex-start",
          gap: "2px",
          boxSizing: "border-box",
          overflow: "hidden",
          cursor: onExpand && !inPopup ? "pointer" : "default",
          // position:relative so the absolute copy+play column anchors
          // here, NOT to a higher parent. Keeps the buttons inside this
          // header's 64px box and out of the meta line's flex flow.
          position: "relative",
          // Header sticky behavior in the popup is implemented via CSS
          // rules in globals.css keyed on .card-expand-popup-body.is-stuck
          // — see CardExpandPopup. position:sticky failed in Safari; the
          // CSS path switches the header to position:absolute when stuck.
        }}
      >
        {/* Top row: trophy + score line / "Judging…" + spinner / copy */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          minHeight: "16px",
          // Reserve room for the absolute copy+play column (only shown when
          // complete) so a long score line (e.g. "OpenAI: 9/10 | A: 7/10 |
          // G: 2/10") clears the buttons instead of sliding under them.
          paddingRight: isComplete && fullCopyText ? 22 : 0,
        }}>
          <TrophyIcon color={trophyColor} />

          {/* Hidden height anchor — keeps the row at caption-semibold's natural
              height regardless of icon glyph metrics. Mirrors iOS's
              hidden Text("X").font(.caption.weight(.semibold)) anchor. */}
          <span
            aria-hidden
            style={{
              width: 0,
              overflow: "hidden",
              fontSize: "12px",
              fontWeight: 600,
              lineHeight: 1.4,
              visibility: "hidden",
            }}
          >
            X
          </span>

          {hasScores && verdict ? (
            <span style={{
              fontSize: "12px",
              fontWeight: 600,
              lineHeight: 1.4,
              // Truncate with "…" rather than wrap/overlap when the score
              // line is wider than the header can hold (minWidth:0 lets this
              // flex child shrink below its content width).
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}>
              {scoreEntries.map((entry, idx) => {
                const label = entry.isWinner ? entry.name : entry.name.charAt(0);
                return (
                  <span key={idx}>
                    {idx > 0 && (
                      <span style={{ color: "var(--text-tertiary)", fontWeight: 400 }}> | </span>
                    )}
                    <span style={{ color: entry.isWinner ? winnerColor : "var(--text-secondary)" }}>
                      {label}: {entry.score}/10
                    </span>
                  </span>
                );
              })}
            </span>
          ) : verdict && !isLoading ? (
            isTiedVerdict && verdict.winners ? (
              // Multi-winner: "Tied: X & Y" in primary text color.
              // Hit when scores dict is missing — rare in practice but
              // possible for legacy / partial-data verdicts that carry
              // winners metadata but no per-provider scores.
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "var(--text-primary)",
                  lineHeight: 1.4,
                }}
              >
                Tied:{" "}
                {verdict.winners
                  .map((pid) => providers.find((p) => p.id === pid)?.display_name || pid)
                  .join(" & ")}
              </span>
            ) : (
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: winnerColor,
                  lineHeight: 1.4,
                }}
              >
                {winnerProvider?.display_name || verdict.winner} wins
              </span>
            )
          ) : (
            <span
              style={{
                fontSize: "12px",
                fontWeight: 600,
                color: rotatingColor || judgeColor,
                lineHeight: 1.4,
                transition: "color 0.6s ease-in-out",
              }}
              >
                Judging… {elapsedSeconds}s
              </span>
          )}

          <span style={{ flex: 1 }} />

          {(isLoading || isWaitingForOutputs) && (
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              style={{ animation: "spin 1.2s linear infinite" }}
            >
              <circle cx="8" cy="8" r="6.5" stroke={judgeColor} strokeWidth="1.5" opacity="0.25" />
              <path
                d="M14.5 8a6.5 6.5 0 00-6.5-6.5"
                stroke={judgeColor}
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          )}
        </div>

        {/* Combined meta line — wraps to 2 lines at 10px monospaced (matches
            ResponseCard Row 2). The layout effect above progressively shortens
            the line via metaLevel (with-model → no-model → no-tokens → abbreviated) until
            it fits the 2-line clamp; ellipsis is the final fallback. */}
        <span
          ref={metaRef}
          style={{
            fontSize: `${META_FONT_SIZE}px`,
            color: "var(--text-secondary)",
            fontFamily:
              "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
            lineHeight: 1.4,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical" as const,
            overflow: "hidden",
            textOverflow: "ellipsis",
            wordBreak: "break-word",
            // Reserve horizontal space for the absolute-positioned
            // button column so the meta line doesn't slide under it.
            // Only when complete — the buttons aren't shown until then.
            paddingRight: isComplete && fullCopyText ? 22 : 0,
          }}
        >
          {metaLine || " "}
        </span>

        {/* Absolute-positioned copy + play column. Anchored to the
            header (position:relative). Sits outside Row 1 / meta
            line's flex flow so its stacked height cannot expand the
            header or squeeze the 2-line meta clamp. Gated on isComplete
            (not just verdict) so the copy button doesn't appear — and
            collide with the loading spinner — while the panel is still
            scoring / synthesizing. */}
        {isComplete && fullCopyText && (
          <div style={{
            position: "absolute",
            top: 4,
            right: 7,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 2,
          }}>
            <button
              onClick={(e) => { e.stopPropagation(); handleCopyText(fullCopyText); }}
              className="btn-ghost"
              style={{ padding: "2px", lineHeight: 0, opacity: 0.75 }}
              title="Copy full card"
              aria-label="Copy full card"
            >
              {didCopy ? (
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M13.25 4.75l-6.5 6.5-3.5-3.5" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" />
                  <path d="M10.5 5.5V3.5a1.5 1.5 0 00-1.5-1.5H3.5A1.5 1.5 0 002 3.5V9a1.5 1.5 0 001.5 1.5h2" />
                </svg>
              )}
            </button>
            {!!onReadAloud && !!recordId && !!verdict.synthesis && (
              <button
                onClick={(e) => handleReadAloudJudge(e)}
                className="btn-ghost"
                style={{
                  padding: "2px",
                  lineHeight: 0,
                  opacity: isTTSActive ? 1 : 0.5,
                  color: isTTSActive ? (isComplete ? winnerColor : judgeColor) : undefined,
                }}
                title={isTTSActive ? "Playing" : isTTSCached ? "Play synthesis (cached)" : "Play synthesis (will synthesize)"}
                aria-label={isTTSActive ? "Currently playing" : "Read synthesis aloud"}
              >
                {isTTSActive ? (
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                    <rect x="3" y="6" width="2" height="4" rx="0.5" />
                    <rect x="7" y="3" width="2" height="10" rx="0.5" />
                    <rect x="11" y="5" width="2" height="6" rx="0.5" />
                  </svg>
                ) : isTTSCached ? (
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M5 3.5l7 4.5-7 4.5V3.5z" />
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 3.5l7 4.5-7 4.5V3.5z" />
                  </svg>
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Body — three regimes: pre-scores spinner; synth-streaming rationale+spinner; complete.
          All three use the SAME height calc as ResponseCard's body, so the
          judge card matches provider-card height exactly across all states
          (waiting, scoring, synthesizing, complete). */}
      {isPreScoringSpinner ? (
        <div
          className="judge-card-body"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            padding: "24px 7px",
            ...(inPopup
              // Body owns the only scroll inside the popup card.
              ? { flex: 1, minHeight: 0, overflowY: "auto" as const }
              : { height: bodyHeight, overflowY: "auto" as const }),
            overscrollBehavior: "auto",
            // Nudge spinner+text block up so it reads as optically centered.
            // Mirrors iOS JudgeCardView .offset(y: -20). Without this, the
            // meta row and scoring bar above pull visual weight up and the
            // spinner looks like it's sitting too low.
            transform: "translateY(-12px)",
          }}
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 16 16"
            fill="none"
            style={{ animation: "spin 1.2s linear infinite" }}
          >
            <circle cx="8" cy="8" r="6.5" stroke={judgeColor} strokeWidth="1.5" opacity="0.25" />
            <path
              d="M14.5 8a6.5 6.5 0 00-6.5-6.5"
              stroke={judgeColor}
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          <span style={{ fontSize: "14px", color: "var(--text-tertiary)", fontStyle: "italic" }}>
            {bodyStatusText}
          </span>
        </div>
      ) : isLoading && verdict && hasScores ? (
        // Synthesis streaming. Two sub-states:
        //   (a) verdict.synthesis is empty — thinking-tier synthesis
        //       models (e.g. Anthropic Sonnet 4.6) often spend 10-90s
        //       in a thinking phase before emitting any text deltas.
        //       Show the big centered spinner with "Synthesizing..." so
        //       the user knows something is happening.
        //   (b) verdict.synthesis is non-empty — text is actively
        //       streaming via synthesis_delta SSE events. Render the
        //       partial text via MarkdownRenderer (same as the completed
        //       branch) with a small "Synthesizing..." caption pinned
        //       below to signal more is coming. Same fix iOS shipped
        //       on 2026-05-14; was a known UX gap (web user stared at
        //       spinner for 30s-3min while text accumulated silently).
        <div
          className="judge-card-body"
          style={{
            fontSize: "14px",
            lineHeight: 1.6,
            padding: "12px 7px",
            ...(inPopup
              // Body owns the only scroll inside the popup card.
              ? { flex: 1, minHeight: 0, overflowY: "auto" as const }
              : { height: bodyHeight, overflowY: "auto" as const }),
            overscrollBehavior: "auto",
          }}
        >
          {verdict.rationale && (
            <p style={{ color: "var(--text-secondary)", margin: "0 0 12px 0", fontStyle: "italic" }}>
              {verdict.rationale}
            </p>
          )}
          {verdict.synthesis && verdict.synthesis.length > 0 ? (
            <div style={{ borderTop: "1px solid var(--border)", paddingTop: "12px" }}>
              <div
                style={{
                  fontSize: "11px",
                  color: "var(--text-tertiary)",
                  marginBottom: "8px",
                  fontWeight: 600,
                  textTransform: "uppercase" as const,
                  letterSpacing: "0.5px",
                }}
              >
                Best-of Synthesis
              </div>
              <MarkdownRenderer content={verdict.synthesis} inPopup={inPopup} />
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  marginTop: "10px",
                  color: "var(--text-tertiary)",
                  fontSize: "12px",
                  fontStyle: "italic",
                }}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 16 16"
                  fill="none"
                  style={{ animation: "spin 1.2s linear infinite", flexShrink: 0 }}
                >
                  <circle cx="8" cy="8" r="6.5" stroke={judgeColor} strokeWidth="1.5" opacity="0.25" />
                  <path
                    d="M14.5 8a6.5 6.5 0 00-6.5-6.5"
                    stroke={judgeColor}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                <span>Synthesizing…</span>
              </div>
            </div>
          ) : (
            <div
              style={{
                borderTop: "1px solid var(--border)",
                paddingTop: "12px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 16 16"
                fill="none"
                style={{ animation: "spin 1.2s linear infinite" }}
              >
                <circle cx="8" cy="8" r="6.5" stroke={judgeColor} strokeWidth="1.5" opacity="0.25" />
                <path
                  d="M14.5 8a6.5 6.5 0 00-6.5-6.5"
                  stroke={judgeColor}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              <span style={{ fontSize: "13px", color: "var(--text-tertiary)", fontStyle: "italic" }}>
                Synthesizing…
              </span>
            </div>
          )}
        </div>
      ) : verdict ? (
        <div
          className="judge-card-body"
          style={{
            fontSize: "14px",
            lineHeight: 1.6,
            padding: "12px 7px",
            ...(inPopup
              // Body owns the only scroll inside the popup card.
              ? { flex: 1, minHeight: 0, overflowY: "auto" as const }
              : { height: bodyHeight, overflowY: "auto" as const }),
            overscrollBehavior: "auto",
          }}
        >
          {verdict.rationale && (
            <p style={{ color: "var(--text-secondary)", margin: "0 0 12px 0", fontStyle: "italic" }}>
              {verdict.rationale}
            </p>
          )}
          {verdict.synthesis && (
            <div style={{ borderTop: "1px solid var(--border)", paddingTop: "12px" }}>
              <div
                style={{
                  fontSize: "11px",
                  color: "var(--text-tertiary)",
                  marginBottom: "8px",
                  fontWeight: 600,
                  textTransform: "uppercase" as const,
                  letterSpacing: "0.5px",
                }}
              >
                Best-of Synthesis
              </div>
              <MarkdownRenderer content={verdict.synthesis} inPopup={inPopup} />
            </div>
          )}
        </div>
      ) : null}

      {/* Context menu (right-click on desktop, long-press on touch) */}
      {menuOpen && menuPos && verdict && (
        <div
          style={{
            position: "fixed",
            top: menuPos.y,
            left: menuPos.x,
            zIndex: 9999,
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: "8px",
            padding: "4px",
            minWidth: "160px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => {
              handleCopyText(fullCopyText);
              setMenuOpen(false);
            }}
            className="btn-ghost"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              width: "100%",
              padding: "6px 12px",
              fontSize: "13px",
              textAlign: "left",
              border: "none",
              background: "transparent",
              cursor: "pointer",
            }}
          >
            Copy
          </button>
          {onRegenerate && (
            <button
              onClick={() => {
                onRegenerate();
                setMenuOpen(false);
              }}
              className="btn-ghost"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                width: "100%",
                padding: "6px 12px",
                fontSize: "13px",
                textAlign: "left",
                border: "none",
                background: "transparent",
                cursor: "pointer",
              }}
            >
              Regenerate
            </button>
          )}
          {promptHash && onSetStatsExcluded && !isStatsExcluded && (
            <button
              onClick={() => {
                onSetStatsExcluded(promptHash, true);
                setMenuOpen(false);
              }}
              className="btn-ghost"
              style={{
                display: "flex", alignItems: "center", gap: "8px", width: "100%", padding: "6px 12px",
                fontSize: "13px", textAlign: "left", border: "none", background: "transparent", cursor: "pointer",
              }}
            >
              Remove from stats
            </button>
          )}
          {promptHash && onSetStatsExcluded && isStatsExcluded && (
            <button
              onClick={() => {
                onSetStatsExcluded(promptHash, false);
                setMenuOpen(false);
              }}
              className="btn-ghost"
              style={{
                display: "flex", alignItems: "center", gap: "8px", width: "100%", padding: "6px 12px",
                fontSize: "13px", textAlign: "left", border: "none", background: "transparent", cursor: "pointer",
              }}
            >
              Count in stats
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => {
                onDelete();
                setMenuOpen(false);
              }}
              className="btn-ghost"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                width: "100%",
                padding: "6px 12px",
                fontSize: "13px",
                textAlign: "left",
                border: "none",
                background: "transparent",
                color: "var(--error)",
                cursor: "pointer",
              }}
            >
              Delete
            </button>
          )}
        </div>
      )}

      {/* TTS playback progress bar — only when the judge card's
          synthesis is the active TTS target. Sits at the bottom of
          the outer card, no layout impact when inactive. */}
      {isTTSActive && typeof ttsProgress === "number" && (
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: 2,
            background: "color-mix(in srgb, " + (isComplete ? winnerColor : judgeColor) + " 20%, transparent)",
            pointerEvents: "none",
            borderRadius: "0 0 var(--radius-lg) var(--radius-lg)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${Math.min(100, Math.max(0, ttsProgress * 100))}%`,
              background: isComplete ? winnerColor : judgeColor,
              transition: "width 120ms linear",
            }}
          />
        </div>
      )}
    </div>
  );
}
